import { formatShortDate } from '@packages/format';
import { LEDGER_SECTION_LABELS, LEDGER_SECTION_ORDER } from './ledger.js';
import { asOfSeriesColumns, mergeSeriesAccounts, } from './series-presentation.js';
export function trialBalanceSeriesColumns(series) {
    return asOfSeriesColumns(series.columns, formatShortDate);
}
/**
 * Section rows in chart order, then the two totals every trial balance is read
 * for.
 *
 * **Sections band the listing but carry NO subtotal**, matching the single-date
 * report exactly. A per-section subtotal is a balance-sheet or income-statement
 * device; on a trial balance it invites the reader to treat a section's net as a
 * figure that means something, when the only figure this report asserts is that
 * total debits equal total credits.
 *
 * **Figures are SIGNED, one column per date - not a Debit/Credit pair per
 * date.** The single-date report splits each balance into two columns
 * (`trialBalanceColumns`), and that is right when there is one date: the split
 * is the whole convention. Repeating it across a series would put 26 money
 * columns on a twelve-month view, which no page and no screen can carry, and
 * would bury the comparison the series exists to make. A signed column reads as
 * one number per date, and the sign carries the same information: positive is a
 * debit balance, negative a credit.
 *
 * The `Total debits` / `Total credits` rows are what preserve the report's
 * essential assertion per column. They come from each column's own
 * `debitTotalMinorUnits` / `creditTotalMinorUnits` rather than being re-derived
 * here, so a column's totals are the same figures its single-date sibling would
 * show, and a date where the two disagree is visible at a glance - which is the
 * main thing a multi-date trial balance is for.
 */
export function buildTrialBalanceSeriesTableRows(columns) {
    const rows = [];
    for (const section of LEDGER_SECTION_ORDER) {
        const accountsByColumn = columns.map((column) => column.statement.accounts.filter((account) => account.section === section));
        // A section absent from EVERY column is absent from the chart (or wholly
        // zero and suppressed) - drop it. A section present in only some columns
        // stays: an account opened part-way through the series is a real row that
        // reads as zero before it existed.
        if (accountsByColumn.every((accounts) => accounts.length === 0))
            continue;
        rows.push({
            kind: 'section',
            section,
            label: LEDGER_SECTION_LABELS[section],
            accounts: mergeSeriesAccounts(accountsByColumn),
        });
    }
    rows.push({
        kind: 'total',
        label: 'Total debits',
        amountsMinorUnits: columns.map((column) => column.statement.debitTotalMinorUnits),
    });
    rows.push({
        kind: 'total',
        label: 'Total credits',
        amountsMinorUnits: columns.map((column) => column.statement.creditTotalMinorUnits),
    });
    return rows;
}
/**
 * Whether every column of a trial-balance series balances.
 *
 * The renderers flag an unbalanced column rather than failing: an out-of-balance
 * trial balance is a fact about the books that the report exists to SHOW, and
 * refusing to render it would hide exactly the thing the reader needs.
 */
export function unbalancedTrialBalanceSeriesColumns(columns) {
    return columns.flatMap((column, index) => column.statement.debitTotalMinorUnits === column.statement.creditTotalMinorUnits ? [] : [index]);
}
